import React, { useEffect, useRef } from "react"
import type { UIMessage } from "@ai-sdk/react"
import {
    Box,
    Text,
    componentStyles,
    useColors,
} from "@mallardbay/lib-react-components"

import { useUser } from "~contexts/user-provider"
import COPY from "~config/copy-constants"
import { TEST_IDS } from "~config/constants"
import {
    isHandledChatPart,
    reportUnknownChatPart,
} from "~components/shared/chat/chat-helpers"

interface Props {
    readonly part: UIMessage["parts"][number]
}

export default function ChatMessageDebug({ part }: Props) {
    const { isDeveloper } = useUser()
    const styles = useStyles()
    useReportUnknownPartOnce(part)

    if (isHandledChatPart(part)) return null
    if (!isDeveloper) return null

    return (
        <Box style={styles.root} testId={TEST_IDS.CHAT_MESSAGE_DEBUG}>
            <Text style={styles.label}>
                {COPY.AI_CHAT_DEBUG_UNHANDLED_PART_LABEL}: {part.type}
            </Text>
            <Box as="pre" style={styles.payload}>
                {JSON.stringify(part, null, 2)}
            </Box>
        </Box>
    )
}

// Part objects are re-created on every streaming update, so we dedupe with
// a ref to ensure Sentry only receives one event per mounted part slot.
function useReportUnknownPartOnce(part: UIMessage["parts"][number]) {
    const hasReportedRef = useRef(false)

    useEffect(() => {
        if (hasReportedRef.current) return
        if (isHandledChatPart(part)) return
        hasReportedRef.current = true
        reportUnknownChatPart(part)
    }, [part])
}

function useStyles() {
    const colors = useColors()

    return componentStyles({
        root: {
            borderWidth: "1px",
            borderStyle: "dashed",
            borderColor: colors.border,
            borderRadius: "md",
            backgroundColor: colors.bgSecondary,
            padding: 2,
        },
        label: {
            fontSize: "xs",
            fontWeight: "bold",
            color: colors.secondaryText,
        },
        payload: {
            fontSize: "xs",
            overflowX: "auto",
            color: colors.secondaryText,
        },
    })
}
